import { FC, Suspense, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Class } from '../../models/Class';
import { Roles } from '../../models/User';
import { useAppSelector } from '../../redux/hooks';
import { selectUser } from '../../redux/reducers/userSlice';
import { isUserAdmin } from '../../businessLogicService/isUserAdmin';
import { useGetClassesByRole } from '../../custom-hooks/useGetClassesByRole';
import { useHiddenClassIds } from '../../custom-hooks/useHiddenClassIds';
import { formatClassDisplayName } from '../../utils/formatClassDisplayName';
import HorizontalLoading from '../../assets/HorizontalLoading/HorizontalLoading';
import ClassesRosterLeftSide from './ClassesRosterLeftSide';
import ClassesRostersRightSide from './ClassesRosterRightSide';
import ClassesRosterCleverInfo from './ClassesRosterCleverInfo';

interface Props {
  classId: string;
  onSelectClass: (classId: string) => void;
  onClassLoaded?: (name: string) => void;
}

const ClassesRosterDetail: FC<Props> = ({ classId, onSelectClass, onClassLoaded }) => {
  const navigate = useNavigate();
  const userRedux = useAppSelector(selectUser);
  const isAdmin = isUserAdmin(userRedux);
  const [refetchSingleClassKey, setRefetchSingleClassKey] = useState(0);
  const [selectedClass, setSelectedClass] = useState<Class | null>(null);
  const [visibleControls, setVisibleControls] = useState(false);
  const hiddenClassIds = useHiddenClassIds();

  const { allClassesLoaded, allClassesData, refetch } = useGetClassesByRole(false, false, null, true);
  const hasRetried = useRef(false);

  useEffect(() => {
    hasRetried.current = false;
  }, [classId]);

  useEffect(() => {
    if (hiddenClassIds.has(classId)) {
      navigate('/classes-rosters', { replace: true });
      return;
    }
    const found = (allClassesData ?? []).find(cls => cls?.id === classId);
    if (found) {
      const sectionName = formatClassDisplayName(found);
      setSelectedClass({ ...found, sectionName });
      // Breadcrumb shows only the class's name — not the section/owner's last name that sectionName appends.
      onClassLoaded?.(found.name ?? '');
      return;
    }
    if (allClassesLoaded && !hasRetried.current) {
      hasRetried.current = true;
      refetch();
    }
  }, [allClassesData, allClassesLoaded, classId, hiddenClassIds, navigate, onClassLoaded, refetch]);

  const handleSelectClass = (cls: Class) => {
    setSelectedClass(cls);
    onSelectClass(cls.id);
  };

  const currentUserId = `UserNode:${userRedux?.id}`;
  const isCoTeacherOfClass =
    selectedClass?.users?.some(
      user => user?.id === currentUserId && user?.isActive && user?.roles?.some(role => role?.name === Roles.TEACHER),
    ) ?? false;
  const userOwnerOfClass = isAdmin || currentUserId === selectedClass?.owner?.id || isCoTeacherOfClass;

  if (!allClassesLoaded) {
    return <HorizontalLoading />;
  }

  return (
    <>
      <div className="col-10 col-offset-1 lg:col-12 lg:col-offset-0 nc_sidebar flex justify-content-between align-items-center">
        <h1 data-testid="name-section" className="ml-2 flex align-items-center gap-xs">
          <span>{selectedClass?.name ?? ''}</span>
          {selectedClass?.section && <span className="font-normal tw-text-lg">· {selectedClass.section}</span>}
        </h1>
        {selectedClass?.externalSync && <ClassesRosterCleverInfo />}
      </div>
      <div className="col-offset-1 lg:col-offset-0 lg:col-3">
        <Button
          icon="nc_icon-nc_menu icon-sm"
          aria-label="Open Sidebar"
          tooltip="Open Sidebar"
          rounded
          outlined
          onClick={() => setVisibleControls(!visibleControls)}
          className="lg:hidden"
        />
        <div className={visibleControls ? '' : ' hidden lg:flex'}>
          <ClassesRosterLeftSide
            selectedClass={selectedClass}
            setSelectedClass={handleSelectClass}
            userOwnerOfClass={userOwnerOfClass}
          />
        </div>
      </div>
      <Suspense
        fallback={
          <div className="flex flex-column col-10 col-offset-1 lg:col-9 lg:col-offset-0 nc_sidebar-main">
            <HorizontalLoading />
          </div>
        }
      >
        <ClassesRostersRightSide
          refetchSingleClassKey={refetchSingleClassKey}
          setRefetchSingleClassKey={setRefetchSingleClassKey}
          selectedClass={selectedClass}
          userOwnerOfClass={userOwnerOfClass}
          setSelectedClass={handleSelectClass}
        />
      </Suspense>
    </>
  );
};

export default ClassesRosterDetail;
